/**
 * How a city feels, and what its spare citizens are doing about it.
 *
 * In its own module because the tile governor needs it — to keep a city out of
 * disorder it has to be able to ask "would this arrangement riot?" — and the governor
 * lives in cities/index.ts, which the economy already imports. Nothing here imports the
 * economy or the city index, so the question can be asked from anywhere.
 */

import type { Content } from '../../../content/schema/index.js';
import { tileIndex } from '../state/grid.js';
import { NONE, type City, type GameState } from '../state/types.js';
import { unitIndex, unitsOf } from '../units/index.js';
import { buildingsOf } from './production.js';
import { civEffectTotal } from './wonders.js';
import { governmentEffects, wearyCitizens } from '../government/index.js';

export interface Mood {
  content: number;
  discontented: number;
  /** Citizens kept content by buildings. */
  fromBuildings: number;
  /** Citizens kept content by a garrison. */
  fromGarrison: number;
  /** Citizens kept content by the government (ADR-0059). */
  fromGovernment: number;
  /** Citizens made discontented by war weariness (ADR-0059). */
  fromWeariness: number;
  /** Citizens kept content by entertainers. */
  fromSpecialists: number;
  disorder: boolean;
}

/** Citizens working no tile. They are specialists; see specialistsOf. */
export function spareCitizens(city: City): number {
  return Math.max(0, city.population - city.workedTiles.length);
}

/** Contentment a city has from everything except its specialists. */
function steadyContentment(
  state: GameState,
  content: Content,
  city: City,
): { buildings: number; garrison: number; government: number; weary: number } {
  const rules = governmentEffects(state, content, city.owner);
  let buildings = 0;
  for (const building of buildingsOf(content, city)) buildings += building.effects.contentment ?? 0;
  // Wonders whose reach is the whole civilisation (ADR-0057).
  buildings += civEffectTotal(state, content, city.owner, 'contentment');
  const here = tileIndex(state.map, city.col, city.row);
  // Soldiers keep order; a worker resting in town does not (ADR-0048: defence 0 is
  // what marks a unit that cannot fight).
  const count = unitsOf(state, city.owner).filter(
    (u) => {
      const def = content.units.get(u.type);
      return unitIndex(state.map, u) === here && def.defence > 0 && def.domain === 'land';
    },
  ).length;
  return {
    buildings,
    // Under some governments soldiers keep order; under others the people will not have it.
    garrison: Math.min(rules.maxGarrisonContentment, count * rules.garrisonContentment),
    government: rules.contentment,
    weary: wearyCitizens(state, content, city.owner),
  };
}

/**
 * Content and discontented citizens. War weariness counts against everything that buys
 * contentment, so a city with contentment to spare absorbs some of it.
 */
function feel(
  content: Content,
  population: number,
  bought: number,
  weary = 0,
): { content: number; discontented: number; disorder: boolean } {
  const base = Math.min(population, content.rules.contentment.contentBase);
  const contented = Math.max(0, Math.min(population, base + bought - weary));
  const discontented = population - contented;
  return { content: contented, discontented, disorder: discontented > contented };
}

/**
 * How many of each specialist the city has, indexed by specialist id.
 *
 * A city with a chosen specialist puts every spare citizen into it. A city on Auto
 * makes the fewest entertainers that keep it out of disorder — or all of them, if even
 * that is not enough — and the rest `cities.autoSpecialist`.
 */
export function specialistsOf(state: GameState, content: Content, city: City): number[] {
  const counts = content.specialists.all.map(() => 0);
  const spare = spareCitizens(city);
  if (spare === 0) return counts;

  if (city.specialist !== NONE && city.specialist >= 0 && city.specialist < counts.length) {
    counts[city.specialist] = spare;
    return counts;
  }

  const fallback = content.specialists.id(content.rules.cities.autoSpecialist);
  const entertainer = content.specialists.all.find((s) => s.yields.contentment > 0);
  let entertainers = 0;
  if (entertainer) {
    const steady = steadyContentment(state, content, city);
    while (
      entertainers < spare &&
      feel(
        content,
        city.population,
        steady.buildings + steady.garrison + steady.government + entertainers * entertainer.yields.contentment,
        steady.weary,
      ).disorder
    ) {
      entertainers++;
    }
    counts[entertainer.id] = entertainers;
  }
  counts[fallback] = (counts[fallback] ?? 0) + spare - entertainers;
  return counts;
}

/**
 * Citizens up to `contentBase` are content by default. Everyone past that is
 * discontented until a building, a garrison or an entertainer buys them back. More
 * discontented than content means disorder.
 */
export function mood(state: GameState, content: Content, city: City): Mood {
  const steady = steadyContentment(state, content, city);
  const counts = specialistsOf(state, content, city);
  let fromSpecialists = 0;
  content.specialists.all.forEach((s) => (fromSpecialists += s.yields.contentment * counts[s.id]!));
  const result = feel(content, city.population, steady.buildings + steady.garrison + steady.government + fromSpecialists, steady.weary);
  return {
    ...result,
    fromBuildings: steady.buildings,
    fromGarrison: steady.garrison,
    fromGovernment: steady.government,
    fromWeariness: steady.weary,
    fromSpecialists,
  };
}

export function inDisorder(state: GameState, content: Content, city: City): boolean {
  return mood(state, content, city).disorder;
}

/** Science and gold a city's specialists produce, before any building multiplies them. */
export function specialistOutput(state: GameState, content: Content, city: City): { science: number; gold: number } {
  const counts = specialistsOf(state, content, city);
  let science = 0;
  let gold = 0;
  for (const s of content.specialists.all) {
    science += s.yields.science * counts[s.id]!;
    gold += s.yields.gold * counts[s.id]!;
  }
  return { science, gold };
}
